import { useEffect, useState } from 'react';
import { adminSecurityApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShieldCheck, ArrowsClockwise, CheckCircle, XCircle, Key, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EVENT_TYPES = [
  { value: '__all__', fr: 'Tous', en: 'All' },
  { value: 'login_success', fr: 'Connexion réussie', en: 'Login success' },
  { value: 'login_failure', fr: 'Connexion échouée', en: 'Login failure' },
  { value: 'login_blocked', fr: 'Connexion bloquée (IP)', en: 'Login blocked (IP)' },
  { value: 'brute_force_detected', fr: 'Brute-force détectée', en: 'Brute-force detected' },
  { value: 'ip_unblocked', fr: 'IP débloquée (manuel)', en: 'IP unblocked (manual)' },
  { value: 'logout', fr: 'Déconnexion', en: 'Logout' },
  { value: 'password_changed', fr: 'Mot de passe modifié', en: 'Password changed' },
  { value: 'password_change_failed', fr: 'Échec changement', en: 'Password change failed' },
  { value: 'password_reset_requested', fr: 'Reset demandé', en: 'Reset requested' },
  { value: 'password_reset_completed', fr: 'Reset complété', en: 'Reset completed' },
];

const eventBadge = (type) => {
  const map = {
    login_success: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle },
    login_failure: { color: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
    login_blocked: { color: 'bg-red-100 text-red-800 border-red-300', Icon: XCircle },
    brute_force_detected: { color: 'bg-red-100 text-red-800 border-red-300', Icon: XCircle },
    ip_unblocked: { color: 'bg-amber-50 text-amber-800 border-amber-200', Icon: CheckCircle },
    logout: { color: 'bg-gray-50 text-gray-700 border-gray-200', Icon: CheckCircle },
    password_changed: { color: 'bg-indigo-50 text-indigo-700 border-indigo-200', Icon: Key },
    password_change_failed: { color: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
    password_reset_requested: { color: 'bg-amber-50 text-amber-800 border-amber-200', Icon: EnvelopeSimple },
    password_reset_completed: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: Key },
  };
  return map[type] || { color: 'bg-gray-50 text-gray-700 border-gray-200', Icon: CheckCircle };
};

const AdminSecurity = () => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('__all__');
  const [filterEmail, setFilterEmail] = useState('');
  const [ipBlocks, setIpBlocks] = useState(null);
  const [unblocking, setUnblocking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterType !== '__all__') params.event_type = filterType;
      if (filterEmail.trim()) params.email = filterEmail.trim().toLowerCase();
      const { data: res } = await adminSecurityApi.list(params);
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const loadIpBlocks = async () => {
    try {
      const { data: res } = await adminSecurityApi.listIpBlocks();
      setIpBlocks(res);
    } catch (e) {
      // silent (admin only)
    }
  };

  const handleUnblock = async (ip) => {
    if (!window.confirm(language === 'fr' ? `Débloquer l'IP ${ip} ?` : `Unblock IP ${ip}?`)) return;
    setUnblocking(ip);
    try {
      await adminSecurityApi.unblockIp(ip);
      toast.success(language === 'fr' ? 'IP débloquée' : 'IP unblocked');
      loadIpBlocks();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setUnblocking(null);
    }
  };

  useEffect(() => { load(); loadIpBlocks(); }, []);

  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-[#0F172A] flex items-center gap-3">
            <ShieldCheck size={32} weight="duotone" className="text-[#0052FF]" />
            {language === 'fr' ? 'Journal de sécurité' : 'Security audit log'}
          </h1>
          <p className="font-manrope text-sm text-[#64748B] mt-1">
            {language === 'fr'
              ? '100 derniers événements d\'authentification et de gestion de mot de passe (IP + horodatage).'
              : 'Latest 100 authentication & password-management events (IP + timestamp).'}
          </p>
        </div>
        <Button onClick={load} variant="outline" data-testid="refresh-events-btn">
          <ArrowsClockwise size={16} className="mr-2" />
          {language === 'fr' ? 'Rafraîchir' : 'Refresh'}
        </Button>
      </div>

      {/* Summary chips */}
      {data?.summary && (
        <div className="flex flex-wrap gap-2" data-testid="security-summary">
          {EVENT_TYPES.filter(et => et.value !== '__all__' && (data.summary[et.value] || 0) > 0).map(et => (
            <span
              key={et.value}
              className={`inline-flex items-center gap-1.5 text-xs font-manrope px-3 py-1 rounded-full border ${eventBadge(et.value).color}`}
            >
              <strong>{data.summary[et.value]}</strong> {language === 'fr' ? et.fr : et.en}
            </span>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-manrope text-[#64748B] mb-1 block">{language === 'fr' ? 'Type d\'événement' : 'Event type'}</label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger data-testid="filter-event-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map(et => (
                  <SelectItem key={et.value} value={et.value}>{language === 'fr' ? et.fr : et.en}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-manrope text-[#64748B] mb-1 block">Email</label>
            <Input
              type="email"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              placeholder={language === 'fr' ? 'Filtrer par email' : 'Filter by email'}
              data-testid="filter-email"
            />
          </div>
          <Button onClick={load} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="apply-filters-btn">
            {language === 'fr' ? 'Appliquer' : 'Apply'}
          </Button>
        </CardContent>
      </Card>

      {/* IP Blocks panel (brute-force protection) */}
      <Card className="border border-[#E2E8F0] shadow-none" data-testid="ip-blocks-panel">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" className="text-red-600" />
            {language === 'fr' ? 'Blocages IP (Anti brute-force)' : 'IP blocks (Anti brute-force)'}
            {ipBlocks && (
              <span className="text-xs font-manrope text-[#64748B] ml-2">
                — {ipBlocks.active_count || 0} {language === 'fr' ? 'actif(s)' : 'active'}
              </span>
            )}
          </CardTitle>
          <Button onClick={loadIpBlocks} variant="outline" size="sm" data-testid="refresh-ipblocks-btn">
            <ArrowsClockwise size={14} className="mr-1" />
            {language === 'fr' ? 'Rafraîchir' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {!ipBlocks || ipBlocks.items.length === 0 ? (
            <div className="p-6 text-center text-[#94A3B8] font-manrope text-sm">
              {language === 'fr' ? 'Aucune IP bloquée.' : 'No IPs blocked.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0]">
                  <TableHead>IP</TableHead>
                  <TableHead>{language === 'fr' ? 'Bloqué le' : 'Blocked at'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Bloqué jusqu\'à' : 'Blocked until'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Échecs' : 'Failures'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Dernier email' : 'Last email'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ipBlocks.items.map((b, i) => (
                  <TableRow key={i} className="border-[#E2E8F0]" data-testid={`ipblock-row-${i}`}>
                    <TableCell className="font-mono text-xs">{b.ip}</TableCell>
                    <TableCell className="font-mono text-xs">{fmt(b.blocked_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{fmt(b.blocked_until)}</TableCell>
                    <TableCell className="text-xs">{b.failure_count}</TableCell>
                    <TableCell className="font-mono text-xs">{b.last_email || '—'}</TableCell>
                    <TableCell>
                      <span className={`inline-flex text-xs font-manrope px-2 py-1 rounded-full border ${b.active ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                        {b.active ? (language === 'fr' ? 'Actif' : 'Active') : (language === 'fr' ? 'Expiré' : 'Expired')}
                      </span>
                    </TableCell>
                    <TableCell>
                      {b.active && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={unblocking === b.ip}
                          onClick={() => handleUnblock(b.ip)}
                          data-testid={`unblock-ip-btn-${i}`}
                        >
                          {unblocking === b.ip ? '…' : (language === 'fr' ? 'Débloquer' : 'Unblock')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Events table */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader>
          <CardTitle className="font-outfit text-base">
            {language === 'fr' ? 'Événements' : 'Events'} {data ? `(${data.events.length})` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#94A3B8] font-manrope">{language === 'fr' ? 'Chargement…' : 'Loading…'}</div>
          ) : !data || data.events.length === 0 ? (
            <div className="p-8 text-center text-[#94A3B8] font-manrope">{language === 'fr' ? 'Aucun événement' : 'No events'}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0]">
                  <TableHead>{language === 'fr' ? 'Quand' : 'When'}</TableHead>
                  <TableHead>{language === 'fr' ? 'Événement' : 'Event'}</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>{language === 'fr' ? 'Rôle' : 'Role'}</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>{language === 'fr' ? 'Raison' : 'Reason'}</TableHead>
                  <TableHead>User-Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.events.map((ev, i) => {
                  const { color, Icon } = eventBadge(ev.event_type);
                  return (
                    <TableRow key={i} className="border-[#E2E8F0]" data-testid={`event-row-${i}`}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{fmt(ev.created_at)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-xs font-manrope px-2 py-1 rounded-full border ${color}`}>
                          <Icon size={12} /> {ev.event_type}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{ev.email || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{ev.role || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{ev.ip || '—'}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">{ev.reason || '—'}</TableCell>
                      <TableCell className="text-xs text-[#94A3B8] max-w-xs truncate" title={ev.user_agent}>{ev.user_agent || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSecurity;
